// *********************************************************************************************
// TFS.WorkItemTracking.Extensions.Service.ts
//   Implementation of the WorkItemFormService which is a service that is accessible
//   from extensions.
// *********************************************************************************************
/// <amd-module name="Wit/WorkItemFormExtension/WorkItemFormExtensionService" />
import { getTFSPageData } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssPageContext, RemoteSerializationSettingsKey, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { Level } from "VSS/Platform/Contracts/ClientTrace";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { publishEvent } from "VSS/Platform/Telemetry";
import { trace } from "VSS/Platform/Trace";
import {
    FieldType as ContractsFieldType,
    FieldUsage,
    WorkItemField,
    WorkItemRelation,
    WorkItemRelationType
} from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { DalFields, FieldType, FieldUsages, WorkItemLinkConstants } from "Wit/Common/Generated/Constants";
import { convertPotentialIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemFormExtension/Resources";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/WorkItemModel/OM/";
import { IField, ILink, IWorkItem, WorkItemOptions } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { getWorkItemUrl } from "Wit/WorkItemModel/Utils";
import {
    canSortBy,
    computeAllowedFieldValues,
    convertFieldValueToDisplayString,
    fieldHasList,
    getAllowedFieldValues,
    isQueryable,
    isReadOnly,
    setAllowedFieldValues
} from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { ExternalLink, Hyperlink, WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { IWorkItemFormExtensionService } from "./IWorkItemFormExtensionService";

function isIdentityRef(value: any): value is IdentityRef {
    return !!(value && value.displayName !== undefined);
}

async function getIdentityFieldValue(
    pageContext: IVssPageContext,
    field: IField,
    returnOriginalValue: boolean
): Promise<IdentityRef | undefined | null> {
    if (field && field.fieldDefinition.isIdentity) {
        const identityRef = field.workItem.getFieldValueById(field.fieldDefinition.id);
        const workItemIdentityRef = await convertPotentialIdentityRefFromFieldValue(pageContext, identityRef);

        return workItemIdentityRef && workItemIdentityRef.identityRef;
    } else if (returnOriginalValue) {
        return field.workItem.getFieldValueById(field.fieldDefinition.id);
    }
}

async function convertPotentialIdentityRefToWorkItemIdentityRef(pageContext: IVssPageContext, value: Object): Promise<Object> {
    if (isIdentityRef(value)) {
        const workItemIdentityRef = await convertPotentialIdentityRefFromFieldValue(pageContext, value);
        return workItemIdentityRef && workItemIdentityRef.identityRef;
    } else {
        return value;
    }
}

export class WorkItemFormExtensionService implements IWorkItemFormExtensionService {
    constructor(private _contributionId: string, private _contributionInstanceId: string, private pageContext: IVssPageContext) { }

    public getId(): Promise<number> {
        return Promise.resolve(this._getWorkItem().id);
    }

    public getRevision(): Promise<number> {
        return Promise.resolve(this._getWorkItem().revision.value);
    }

    public getFields(): Promise<WorkItemField[]> {
        return Promise.resolve(this._getWorkItem().fields.map(field => this._mapWorkItemFieldToFieldContract(field)));
    }

    public async getFieldValue(fieldReferenceName: string, options?: WorkItemOptions): Promise<Object>;
    public async getFieldValue(fieldReferenceName: string, returnOriginalValue?: boolean): Promise<Object>;
    public async getFieldValue(fieldReferenceName: string, flagOrOptions?: boolean | WorkItemOptions): Promise<Object> {
        const { returnOriginalValue } = this._getFieldOptions(flagOrOptions);
        const workItem = this._getWorkItem();

        return workItem.getFieldValueByName(fieldReferenceName, returnOriginalValue);
    }

    public async getIdentityFieldValue(fieldReferenceName: string, options?: WorkItemOptions): Promise<IdentityRef | undefined | null> {
        const { returnOriginalValue } = this._getFieldOptions(options);
        const workItem = this._getWorkItem();
        const field = workItem.getFieldValueByName(fieldReferenceName);

        return getIdentityFieldValue(this.pageContext, field, returnOriginalValue);
    }

    public async getFieldValues(fieldReferenceNames: string[], options?: WorkItemOptions): Promise<{ [key: string]: Object }>;
    public async getFieldValues(fieldReferenceNames: string[], returnOriginalValue?: boolean): Promise<{ [key: string]: Object }>;
    public async getFieldValues(fieldReferenceNames: string[], flagOrOptions?: boolean | WorkItemOptions): Promise<{ [key: string]: Object }> {
        const { returnOriginalValue } = this._getFieldOptions(flagOrOptions);
        const workItem = this._getWorkItem();
        const fieldValues: { [key: string]: Object } = {};
        if (fieldReferenceNames instanceof Array) {
            fieldReferenceNames.forEach(fieldName => {
                fieldValues[fieldName] = workItem.getFieldValueByName(fieldName, returnOriginalValue);
            });
        }

        return fieldValues;
    }

    public async setFieldValue(fieldReferenceName: string, value: Object): Promise<boolean> {
        const workItem = this._getWorkItem();
        if (workItem.isReadOnly()) {
            return false;
        }

        const fieldValue = await convertPotentialIdentityRefToWorkItemIdentityRef(this.pageContext, value);
        return workItem.setFieldValueByName(fieldReferenceName, fieldValue);
    }

    public async setFieldValues(fields: { [key: string]: Object }): Promise<{ [key: string]: boolean }> {
        const workItem = this._getWorkItem();
        const success: { [key: string]: boolean } = {};
        const readonly = this._getWorkItem().isReadOnly();
        if (fields) {
            for (const field in fields) {
                if (readonly) {
                    success[field] = false;
                } else {
                    const fieldValue = await convertPotentialIdentityRefToWorkItemIdentityRef(this.pageContext, fields[field]);
                    success[field] = workItem.setFieldValueByName(field, fieldValue);
                }
            }
        }

        return success;
    }

    public async getAllowedFieldValues(fieldReferenceName: string): Promise<Object[]> {
        const workItem = this._getWorkItem();
        const field: IField | undefined = workItem.getFieldByName(fieldReferenceName);

        if (field) {
            return computeAllowedFieldValues(field, workItem);
        } else {
            return [];
        }
    }

    public async filterAllowedFieldValues(fieldReferenceName: string, values: string[]): Promise<void> {
        try {
            publishEvent(this.pageContext, WITCustomerIntelligenceArea.WORK_ITEM_TRACKING, WITCustomerIntelligenceFeature.FILTER_METHOD_USAGE, {});
        } catch (error) { }

        const workItem = this._getWorkItem();

        const field = workItem.getFieldByName(fieldReferenceName);
        if (!field) {
            return;
        }

        const isDateParsingFixEnabled = !isFeatureFlagEnabled(this.pageContext, FeatureFlags.DateParsingFixDisabled, false);

        const allowedValues: string[] = computeAllowedFieldValues(field, workItem).map(v => convertFieldValueToDisplayString(v, isDateParsingFixEnabled));

        if (fieldHasList(field) && !field.fieldDefinition.isIdentity) {
            const allowedValuesSet = new Set<string>();
            allowedValues.forEach(value => {
                allowedValuesSet.add(value);
            });
            values = values.filter(value => allowedValuesSet.has(value));
            setAllowedFieldValues(field, values);
            // TODO:Replat
            //workItem.fireFieldAllowedValuesChange(fieldReferenceName);
        }
    }

    public async getFilteredAllowedFieldValues(fieldReferenceName: string): Promise<string[]> {
        const field: IField | undefined = this._getWorkItem().getFieldByName(fieldReferenceName);
        if (field) {
            return getAllowedFieldValues(field, field.workItem).value;
        } else {
            return [];
        }
    }

    public isDirty(): Promise<boolean> {
        return Promise.resolve(this._getWorkItem().dirty.value);
    }

    public isNew(): Promise<boolean> {
        return Promise.resolve(this._getWorkItem().isNew());
    }

    public isValid(): Promise<boolean> {
        return Promise.resolve(this._getWorkItem().isValid());
    }

    public setError(errorMessage: string): Promise<void> {
        if (this._getWorkItem().isReadOnly()) {
            return Promise.reject(Resources.WorkItemFormIsReadonly);
        }

        const error = errorMessage || format(Resources.ContributionBlockingWITSave, this._contributionId);
        this._getWorkItem().setContributionErrorStatus(this._contributionInstanceId, false, error);

        return Promise.resolve();
    }

    public clearError(): Promise<void> {
        this._getWorkItem().setContributionErrorStatus(this._contributionInstanceId, true);

        return Promise.resolve();
    }

    public save(): Promise<void> {
        if (this._getWorkItem().isReadOnly()) {
            return Promise.reject(Resources.WorkItemFormIsReadonly);
        }
        return new Promise((resolve, reject) => {
            this.beginSaveWorkItem(resolve, reject);
        });
    }

    public async refresh(): Promise<void> {
        return new Promise((resolve, reject) => {
            const activeWorkItem = this._getWorkItem();
            if (activeWorkItem.isNew()) {
                reject(Resources.ErrorExtensionService_CannotRefreshWorkItem);
                return;
            }

            const refreshWorkItem = () => {
                activeWorkItem.refresh().catch(reason => reject(reason));
                resolve();
            };
            if (activeWorkItem.dirty.value) {
                showMessageDialog(this.pageContext, {
                    message: Resources.ConfirmWorkItemRefresh,
                    onClose: result => result && refreshWorkItem()
                });
            } else {
                refreshWorkItem();
            }
        });
    }

    public async reset(): Promise<void> {
        if (this._getWorkItem().isReadOnly()) {
            return Promise.reject(Resources.WorkItemFormIsReadonly);
        }

        return new Promise((resolve: (value?: void | PromiseLike<void> | undefined) => void, reject: (reason?: any) => void) => {
            const activeWorkItem = this._getWorkItem();

            if (!activeWorkItem.dirty.value || activeWorkItem.isNew() || activeWorkItem.saving.value) {
                reject(Resources.ErrorExtensionService_CannotRevertWorkItem);
                return;
            }

            showMessageDialog(this.pageContext, {
                message: Resources.ConfirmWorkItemRevert,
                okButtonProps: { text: Resources.Revert },
                onClose: result => {
                    if (result) {
                        this._getWorkItem().reset();
                        resolve();
                    }
                    reject(result);
                }
            });
        });
    }

    public getInvalidFields(): Promise<WorkItemField[]> {
        return Promise.resolve(
            this._getWorkItem()
                .getInvalidFields()
                .map(field => this._mapWorkItemFieldToFieldContract(field))
        );
    }

    public getDirtyFields(includeSystemChanges?: boolean): Promise<WorkItemField[]> {
        return Promise.resolve(
            this._getWorkItem()
                .getDirtyFields(!includeSystemChanges)
                .map(field => this._mapWorkItemFieldToFieldContract(field))
        );
    }

    public async addWorkItemRelations(workItemRelations: WorkItemRelation[]): Promise<void> {
        if (this._getWorkItem().isReadOnly()) {
            return Promise.resolve();
        }

        if (workItemRelations instanceof Array) {
            const activeWorkItem = this._getWorkItem();
            for (const workItemRelation of workItemRelations) {
                const comment = workItemRelation.attributes ? workItemRelation.attributes["comment"] : null;

                if (this._isResourceLinkTypeFromRelation(workItemRelation)) {
                    const registeredLinkType = workItemRelation.attributes ? workItemRelation.attributes["name"] : null;
                    let resourceLink: ILink | undefined;
                    switch (workItemRelation.rel) {
                        case WorkItemLinkConstants.HYPERLINKLINKTYPE:
                            resourceLink = Hyperlink.create(workItemRelation.url, comment);
                            break;
                        case WorkItemLinkConstants.ARTIFACTLINKTYPE:
                            resourceLink = ExternalLink.create(registeredLinkType, workItemRelation.url, comment);
                            break;
                        default:
                            break;
                    }

                    if (resourceLink) {
                        activeWorkItem.addLinks([resourceLink]);
                    } else {
                        throw new Error(Resources.AttachmentsNotSupported);
                    }
                } else if (await this._isRemoteLinkType(workItemRelation)) {
                    try {
                        await this._addRemoteWorkItemRelation(workItemRelation);
                    } catch (error) {
                        throw error;
                    }
                } else {
                    const targetWorkItemId = this._getWorkItemIdFromUrl(workItemRelation.url);
                    activeWorkItem.addLinks([await WorkItemLink.create(this.pageContext, workItemRelation.rel, targetWorkItemId, comment)]);
                }
            }
        }

        return Promise.resolve();
    }

    public async removeWorkItemRelations(workItemRelations: WorkItemRelation[]): Promise<void> {
        if (this._getWorkItem().isReadOnly()) {
            return Promise.resolve();
        }

        if (!(workItemRelations instanceof Array)) {
            throw new Error(Resources.InvalidArgumentExpectingArray);
        }

        const activeWorkItem = this._getWorkItem();
        const links: ILink[] = [];
        const existingLinks = activeWorkItem.getLinks();

        for (const workItemRelation of workItemRelations) {
            const linkType = this._getRelationLinkTypeFromRelation(workItemRelation);
            let workItemId: number = -1;
            if (linkType === DalFields.AttachedFiles) {
                throw new Error(Resources.AttachmentsNotSupported);
            }

            if (linkType === DalFields.RelatedLinks) {
                workItemId = this._getWorkItemIdFromUrl(workItemRelation.url);
            }

            for (const existingLink of existingLinks) {
                if (!existingLink.isRemoved() && linkType === existingLink.linkData.FldID) {
                    if (existingLink.linkData.FldID === DalFields.RelatedLinks) {
                        const workItemLink = existingLink;
                        const linkTypeEndId = workItemLink.getWitLinkTypeEndId();
                        if (linkTypeEndId) {
                            const linkTypeEnd: IWorkItemLinkTypeEnd = await WorkItemLink.findLinkTypeEnd(this.pageContext, linkTypeEndId);

                            if (
                                workItemRelation.rel.toUpperCase() === linkTypeEnd.immutableName.toUpperCase() &&
                                workItemId === existingLink.linkData.ID
                            ) {
                                links.push(existingLink);
                                break;
                            }
                        }
                    } else {
                        const filePath = existingLink.linkData?.FilePath?.toUpperCase();
                        if (workItemRelation.url.toUpperCase() === filePath) {
                            links.push(existingLink);
                            break;
                        }
                    }
                }
            }
        }

        activeWorkItem.removeLinks(links);

        return Promise.resolve();
    }

    public async getWorkItemRelations(): Promise<WorkItemRelation[]> {
        const activeWorkItem = this._getWorkItem();
        const existingLinks = activeWorkItem.getLinks();
        const filteredLinks = existingLinks.filter((workItemLink: ILink) => {
            return !workItemLink.isRemoved() && workItemLink.linkData.FldID !== DalFields.AttachedFiles;
        });
        return Promise.all(
            filteredLinks.map(async link => {
                const relation: WorkItemRelation = {
                    attributes: link.getAttributes(),
                    rel: await link.getRelationType(this.pageContext),
                    url: await link.getLinkUrl(this.pageContext, getTFSPageData(this.pageContext)!.project!.id)
                };
                return relation;
            })
        );
    }

    public async getWorkItemResourceUrl(workItemId: number): Promise<string> {
        return Promise.resolve(getWorkItemUrl(this.pageContext, getTFSPageData(this.pageContext)!.project!.id, workItemId));
    }

    public async getWorkItemRelationTypes(): Promise<WorkItemRelationType[]> {
        const workItemRelationTypes: WorkItemRelationType[] = [];
        const workItemLinkTypes = await WorkItemLink.getLinkTypes(this.pageContext);

        for (const workItemLinkType of workItemLinkTypes) {
            workItemRelationTypes.push({
                attributes: {
                    [WorkItemLinkConstants.ATTRIBUTES_USAGE]: WorkItemLinkConstants.WORKITEMLINKUSAGE,
                    [WorkItemLinkConstants.ATTRIBUTES_EDITABLE]: workItemLinkType.canEdit,
                    [WorkItemLinkConstants.ATTRIBUTES_ENABLED]: workItemLinkType.isActive,
                    [WorkItemLinkConstants.ATTRIBUTES_ACYCLIC]: workItemLinkType.isNonCircular,
                    [WorkItemLinkConstants.ATTRIBUTES_DIRECTIONAL]: workItemLinkType.isDirectional,
                    [WorkItemLinkConstants.ATTRIBUTES_SINGLETARGET]: workItemLinkType.isOneToMany,
                    [WorkItemLinkConstants.ATTRIBUTES_TOPOLOGY]: workItemLinkType.topology
                },
                referenceName: workItemLinkType.forwardEnd.immutableName,
                name: workItemLinkType.forwardEnd.name,
                url: "",
                _links: null
            });

            if (workItemLinkType.forwardEnd.isForwardLink !== workItemLinkType.reverseEnd.isForwardLink) {
                workItemRelationTypes.push({
                    attributes: {
                        [WorkItemLinkConstants.ATTRIBUTES_USAGE]: WorkItemLinkConstants.WORKITEMLINKUSAGE,
                        [WorkItemLinkConstants.ATTRIBUTES_EDITABLE]: workItemLinkType.canEdit,
                        [WorkItemLinkConstants.ATTRIBUTES_ENABLED]: workItemLinkType.isActive,
                        [WorkItemLinkConstants.ATTRIBUTES_ACYCLIC]: workItemLinkType.isNonCircular,
                        [WorkItemLinkConstants.ATTRIBUTES_DIRECTIONAL]: workItemLinkType.isDirectional,
                        [WorkItemLinkConstants.ATTRIBUTES_SINGLETARGET]: workItemLinkType.isOneToMany,
                        [WorkItemLinkConstants.ATTRIBUTES_TOPOLOGY]: workItemLinkType.topology
                    },
                    referenceName: workItemLinkType.reverseEnd.immutableName,
                    name: workItemLinkType.reverseEnd.name,
                    url: "",
                    _links: null
                });
            }
        }

        for (const resourceLink of WorkItemLinkConstants.RESOURCELINKTYPES) {
            workItemRelationTypes.push({
                attributes: {
                    [WorkItemLinkConstants.ATTRIBUTES_USAGE]: WorkItemLinkConstants.RESOURCELINKUSAGE,
                    [WorkItemLinkConstants.ATTRIBUTES_EDITABLE]: false,
                    [WorkItemLinkConstants.ATTRIBUTES_ENABLED]: true
                },
                referenceName: resourceLink,
                name: "",
                url: "",
                _links: null
            });
        }

        return Promise.resolve(workItemRelationTypes);
    }

    public hasActiveWorkItem(): Promise<boolean> {
        return Promise.resolve(this._hasActiveWorkItem());
    }

    // This is deprecated in favor of the save method. Leaving here to support back compat.
    public async beginSaveWorkItem(successCallback: () => void, errorCallback: (error: Error) => void): Promise<void> {
        if ((await this.isDirty()) && (await this.isValid())) {
            if (this._getWorkItem().isReadOnly()) {
                errorCallback(new Error(Resources.WorkItemFormIsReadonly));
            }
            try {
                await this._getWorkItem().save();
                successCallback();
                publishEvent(this.pageContext, "WIT", "SaveWorkItemFromExtension", {});
                trace(this.pageContext, {
                    area: "WIT",
                    feature: "WorkItem",
                    level: Level.Info,
                    component: "WorkItemFormExtensionService",
                    exceptionType: "",
                    message: "WorkItemExtension.saveComplete",
                    method: "",
                    properties: {
                        action: "WorkItemExtension.saveComplete"
                    }
                });
            } catch (err) {
                errorCallback(err);
            }
        } else {
            errorCallback(new Error(Resources.WorkItemNotSaveable));
        }
    }

    private async _addRemoteWorkItemRelation(workItemRelation: WorkItemRelation): Promise<void> {
        // TODO:Replat
        // waiting on remote link implementation.
        // const activeWorkItem = this._getWorkItem();
        // const comment = workItemRelation.attributes && workItemRelation.attributes.comment;
        // const targetWorkItemId = this._getWorkItemIdFromUrl(workItemRelation.url);
        // let dataProviderData: IRemoteWebApiWorkItemData;
        // try {
        //     dataProviderData = await getRemoteWorkItemByUrl(workItemRelation.url);
        // } catch (error) {
        //     throw new Error(Resources.LinkFormWorkItemNotFound);
        // }
        // if (!dataProviderData) {
        //     throw new Error(Resources.LinkFormWorkItemNotFound);
        // }
        // const remoteLinkContext: RemoteLinkContext = {
        //     remoteHostId: dataProviderData["work-item-host-id"],
        //     remoteHostName: dataProviderData["work-item-host-name"],
        //     remoteHostUrl: dataProviderData["work-item-host-url"],
        //     remoteProjectId: dataProviderData["work-item-project-id"]
        // };
        // const link = await WorkItemLink.create(this.pageContext, workItemRelation.rel, targetWorkItemId, comment, false, remoteLinkContext);
        // if (activeWorkItem.findLink(link)) {
        //     throw new Error(Resources.LinksControlDuplicateRemoteLink);
        // }
        // activeWorkItem.addLinks([link]);
    }

    private _hasActiveWorkItem(): boolean {
        const activeWorkItemService = this.pageContext.getService<ActiveWorkItemService>("ms.vss-work-web.active-work-item-service");
        return !!activeWorkItemService.activeWorkItem;
    }

    private _getWorkItem(): IWorkItem {
        if (!this._hasActiveWorkItem()) {
            throw new Error(Resources.NoActiveWorkItem);
        }

        const activeWorkItemService = this.pageContext.getService<ActiveWorkItemService>("ms.vss-work-web.active-work-item-service");
        return activeWorkItemService.activeWorkItem!;
    }

    private _getWorkItemIdFromUrl(url: string): number {
        if (url) {
            url = url.trim();
            const slashIndex = url.lastIndexOf("/");
            if (slashIndex !== -1 && slashIndex < url.length - 1) {
                const workItemIdString = url.substring(slashIndex + 1);
                const workItemId = parseInt(workItemIdString);
                if (!isNaN(workItemId)) {
                    return workItemId;
                }
            }
        }

        throw new Error(format(Resources.InvalidWorkItemUrl, url));
    }

    private _isResourceLinkTypeFromRelation(workItemRelation: WorkItemRelation): boolean {
        const linkFieldID = this._getRelationLinkTypeFromRelation(workItemRelation);
        return this._isResourceLinkType(linkFieldID);
    }

    private _isResourceLinkType(linkFieldID: number): boolean {
        switch (linkFieldID) {
            case DalFields.BISURI:
            case DalFields.AttachedFiles:
            case DalFields.LinkedFiles:
                return true;
            default:
                return false;
        }
    }

    private async _isRemoteLinkType(workItemRelation: WorkItemRelation): Promise<boolean> {
        const relType: string = workItemRelation.rel.toUpperCase();
        const linkTypeEnd = await WorkItemLink.findLinkTypeEnd(this.pageContext, relType);
        if (linkTypeEnd.linkType) {
            return linkTypeEnd.linkType.isRemote;
        }
        return false;
    }

    private _getRelationLinkTypeFromRelation(workItemRelation: WorkItemRelation): number {
        const relType: string = workItemRelation.rel.toUpperCase();

        switch (relType) {
            case WorkItemLinkConstants.ATTACHEDLINKTYPE.toUpperCase():
                return DalFields.AttachedFiles;
            case WorkItemLinkConstants.HYPERLINKLINKTYPE.toUpperCase():
                return DalFields.LinkedFiles;
            case WorkItemLinkConstants.ARTIFACTLINKTYPE.toUpperCase():
                return DalFields.BISURI;
            default:
                return DalFields.RelatedLinks;
        }
    }

    private _getUsage(field: IField): FieldUsage {
        switch (field.fieldDefinition.usages) {
            case FieldUsages.Tree:
                return FieldUsage.Tree;
            case FieldUsages.WorkItem:
                return FieldUsage.WorkItem;
            case FieldUsages.WorkItemLink:
                return FieldUsage.WorkItemLink;
            case FieldUsages.WorkItemTypeExtension:
                return FieldUsage.WorkItemTypeExtension;
            default:
                return FieldUsage.None;
        }
    }

    private _mapWorkItemFieldToFieldContract(field: IField): WorkItemField {
        const fieldType: ContractsFieldType = this._mapWorkItemFieldType(field.fieldDefinition.type)!;
        return {
            name: field.fieldDefinition.name,
            referenceName: field.fieldDefinition.referenceName,
            readOnly: isReadOnly(field),
            canSortBy: canSortBy(field),
            isQueryable: isQueryable(field),
            supportedOperations: [],
            description: "",
            type: fieldType,
            _links: null,
            isIdentity: field.fieldDefinition.isIdentity,
            isPicklist: false,
            isPicklistSuggested: false,
            picklistId: "",
            usage: this._getUsage(field),
            isDeleted: false,
            url: ""
        };
    }

    private _mapWorkItemFieldType(fieldType: FieldType): ContractsFieldType | null {
        switch (fieldType) {
            case FieldType.Boolean:
                return ContractsFieldType.Boolean;
            case FieldType.DateTime:
                return ContractsFieldType.DateTime;
            case FieldType.Double:
                return ContractsFieldType.Double;
            case FieldType.Guid:
                return ContractsFieldType.Guid;
            case FieldType.History:
                return ContractsFieldType.History;
            case FieldType.Html:
                return ContractsFieldType.Html;
            case FieldType.Integer:
                return ContractsFieldType.Integer;
            case FieldType.Internal:
                return null;
            case FieldType.PlainText:
                return ContractsFieldType.PlainText;
            case FieldType.String:
                return ContractsFieldType.String;
            case FieldType.TreePath:
                return ContractsFieldType.TreePath;
            default:
                return null;
        }
    }

    private _isWorkItemOptions(flagOrOptions: boolean | WorkItemOptions | undefined): flagOrOptions is WorkItemOptions {
        if (flagOrOptions) {
            const options = flagOrOptions as WorkItemOptions;
            return options.returnOriginalValue !== undefined;
        } else {
            return false;
        }
    }

    private _getFieldOptions(flagOrOptions?: boolean | WorkItemOptions): { returnOriginalValue: boolean } {
        let returnOriginalValue = false;
        if (this._isWorkItemOptions(flagOrOptions)) {
            ({ returnOriginalValue } = { ...flagOrOptions });
        } else if (flagOrOptions) {
            returnOriginalValue = flagOrOptions;
        }

        return { returnOriginalValue };
    }
}

interface IWorkItemFormExtensionServiceFactory {
    getInstance(contributionId: string, contributionInstanceId: string): WorkItemFormExtensionService;
}

export class WorkItemFormExtensionServiceFactory extends VssService implements IWorkItemFormExtensionServiceFactory {
    private _serviceInstances: { [key: string]: WorkItemFormExtensionService };

    public _serviceStart(pageContext: IVssPageContext) {
        this._serviceInstances = {};
        this.pageContext = pageContext;
    }

    /**
     * Factory method for creating/getting an instance of the work item form extension service.
     *
     * @param contributionId The id of the contribution
     * @param contributionInstanceId The instance id of the contribution
     */
    public getInstance(contributionId: string, contributionInstanceId: string): WorkItemFormExtensionService {
        const serviceInstanceKey = `${contributionId}-${contributionInstanceId}`;
        let serviceInstance = this._serviceInstances[serviceInstanceKey];
        if (!serviceInstance) {
            serviceInstance = new WorkItemFormExtensionService(contributionId, contributionInstanceId, this.pageContext);
            makeRemotable(serviceInstance);
            this._serviceInstances[serviceInstanceKey] = serviceInstance;
        }

        return serviceInstance;
    }
}

const factoryServiceName = "ms.vss-work-web.work-item-form-extension-service-factory";
Services.add(factoryServiceName, { serviceFactory: WorkItemFormExtensionServiceFactory, options: ServiceOptions.Remotable });

interface IActiveWorkItemService {
    /**
     * Set the active work item.
     *
     * @param {IWorkItem} workItem
     */
    setActiveWorkItem(workItem?: IWorkItem): void;
}

export class ActiveWorkItemService extends VssService implements IActiveWorkItemService {
    public activeWorkItem: IWorkItem | undefined;

    public setActiveWorkItem(workItem?: IWorkItem) {
        this.activeWorkItem = workItem;
    }
}

const serviceName = "ms.vss-work-web.active-work-item-service";
Services.add(serviceName, { serviceFactory: ActiveWorkItemService, options: ServiceOptions.Remotable });

function makeRemotable(obj: any) {
    obj[RemoteSerializationSettingsKey] = {
        proxyFunctions: true,
        ignoredProperties: { pageContext: true }
    };
}
